type CheckoutAddressFields = Record<string, string>;
type CheckoutAddressOption = {
    id: number | string;
    label: string;
    fields: CheckoutAddressFields;
};
type CheckoutAddressOptionsByType = Partial<Record<'billing' | 'shipping', CheckoutAddressOption[]>>;

const selectorQuery = '[data-weka-checkout-address-selector]';
const orderOnBehalfSelectorQuery = '[data-weka-order-on-behalf-selector]';
const initializedSelectors = new WeakSet<HTMLSelectElement>();
const initializedOrderOnBehalfSelectors = new WeakSet<HTMLSelectElement>();

const getJQuery = (): any => (window as any).jQuery || (window as any).$;

const dispatchNativeChange = (element: HTMLElement): void => {
    element.dispatchEvent(new Event('input', { bubbles: true }));
    element.dispatchEvent(new Event('change', { bubbles: true }));

    const $ = getJQuery();
    if ($) {
        $(element).trigger('change');
    }
};

const updateCheckout = (): void => {
    const $ = getJQuery();
    if ($) {
        $(document.body).trigger('update_checkout');
    }
};

const getFieldElement = (fieldName: string): HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null => {
    const byId = document.getElementById(fieldName);
    if (byId instanceof HTMLInputElement || byId instanceof HTMLSelectElement || byId instanceof HTMLTextAreaElement) {
        return byId;
    }

    const byName = document.querySelector(`[name="${fieldName}"]`);
    if (byName instanceof HTMLInputElement || byName instanceof HTMLSelectElement || byName instanceof HTMLTextAreaElement) {
        return byName;
    }

    return null;
};

const setFieldValue = (fieldName: string, value: string): void => {
    const field = getFieldElement(fieldName);
    if (!field) {
        return;
    }

    field.value = value;
    dispatchNativeChange(field);
};

const applyCheckoutFields = (fields: CheckoutAddressFields, shouldRefreshCheckout = true): void => {
    const countryFields = Object.keys(fields).filter((fieldName) => fieldName.endsWith('_country'));
    const stateFields = Object.keys(fields).filter((fieldName) => fieldName.endsWith('_state'));
    const remainingFields = Object.keys(fields).filter((fieldName) => (
        !countryFields.includes(fieldName)
        && !stateFields.includes(fieldName)
    ));

    countryFields.forEach((fieldName) => setFieldValue(fieldName, fields[fieldName] ?? ''));
    remainingFields.forEach((fieldName) => setFieldValue(fieldName, fields[fieldName] ?? ''));

    window.setTimeout(() => {
        stateFields.forEach((fieldName) => setFieldValue(fieldName, fields[fieldName] ?? ''));
        if (shouldRefreshCheckout) {
            updateCheckout();
        }
    }, 50);
};

const setHiddenSelectedAddress = (select: HTMLSelectElement): void => {
    const hiddenName = String(select.dataset.wekaCheckoutAddressHidden || '').trim();
    if (hiddenName === '') {
        return;
    }

    const hidden = document.querySelector<HTMLInputElement>(`[data-weka-checkout-address-hidden-input="${hiddenName}"]`);
    if (hidden) {
        hidden.value = select.value;
    }
};

const getSelectedAddressFields = (select: HTMLSelectElement): CheckoutAddressFields => {
    const selectedOption = select.selectedOptions.item(0);
    const rawAddress = selectedOption?.getAttribute('data-address') || '';
    if (rawAddress === '') {
        return {};
    }

    try {
        const parsed = JSON.parse(rawAddress);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? parsed as CheckoutAddressFields
            : {};
    } catch {
        return {};
    }
};

const applySelectedAddress = (select: HTMLSelectElement, shouldRefreshCheckout = true): void => {
    setHiddenSelectedAddress(select);

    const fields = getSelectedAddressFields(select);
    applyCheckoutFields(fields, shouldRefreshCheckout);
};

const initCheckoutAddressSelector = (select: HTMLSelectElement): void => {
    if (initializedSelectors.has(select)) {
        return;
    }

    initializedSelectors.add(select);
    setHiddenSelectedAddress(select);

    select.addEventListener('change', () => {
        applySelectedAddress(select);
    });
};

export const initCheckoutAddressSelectors = (): void => {
    document.querySelectorAll<HTMLSelectElement>(selectorQuery).forEach(initCheckoutAddressSelector);
};

const getSelectedOrderOnBehalfFields = (select: HTMLSelectElement): CheckoutAddressFields => {
    const selectedOption = select.selectedOptions.item(0);
    const rawFields = selectedOption?.getAttribute('data-checkout-fields') || '';
    if (rawFields === '') {
        return {};
    }

    try {
        const parsed = JSON.parse(rawFields);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? parsed as CheckoutAddressFields
            : {};
    } catch {
        return {};
    }
};

const getSelectedOrderOnBehalfAddresses = (select: HTMLSelectElement): CheckoutAddressOptionsByType => {
    const selectedOption = select.selectedOptions.item(0);
    const rawAddresses = selectedOption?.getAttribute('data-checkout-addresses') || '';
    if (rawAddresses === '') {
        return {};
    }

    try {
        const parsed = JSON.parse(rawAddresses);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? parsed as CheckoutAddressOptionsByType
            : {};
    } catch {
        return {};
    }
};

const createAddressOptionElement = (address: CheckoutAddressOption): HTMLOptionElement | null => {
    const optionValue = String(address.id || '').trim();
    if (optionValue === '') {
        return null;
    }

    const option = document.createElement('option');
    option.value = optionValue;
    option.textContent = String(address.label || 'Address');
    option.setAttribute('data-address', JSON.stringify(address.fields || {}));

    return option;
};

const refreshCheckoutAddressSelectors = (addressesByType: CheckoutAddressOptionsByType): void => {
    document.querySelectorAll<HTMLSelectElement>(selectorQuery).forEach((addressSelect) => {
        const type = addressSelect.dataset.wekaCheckoutAddressSelector === 'shipping' ? 'shipping' : 'billing';
        const placeholder = addressSelect.querySelector<HTMLOptionElement>('option[value=""]')?.textContent || (
            type === 'shipping' ? 'Select a shipping address' : 'Select a billing address'
        );
        const addresses = addressesByType[type] || [];

        addressSelect.replaceChildren();

        const placeholderOption = document.createElement('option');
        placeholderOption.value = '';
        placeholderOption.textContent = placeholder;
        addressSelect.appendChild(placeholderOption);

        addresses.forEach((address) => {
            const option = createAddressOptionElement(address);
            if (option) {
                addressSelect.appendChild(option);
            }
        });

        addressSelect.value = '';
        setHiddenSelectedAddress(addressSelect);
    });
};

const initOrderOnBehalfSelector = (select: HTMLSelectElement): void => {
    if (initializedOrderOnBehalfSelectors.has(select)) {
        return;
    }

    initializedOrderOnBehalfSelectors.add(select);
    select.addEventListener('change', () => {
        refreshCheckoutAddressSelectors(getSelectedOrderOnBehalfAddresses(select));
        applyCheckoutFields(getSelectedOrderOnBehalfFields(select));
    });
};

export const initOrderOnBehalfSelectors = (): void => {
    document.querySelectorAll<HTMLSelectElement>(orderOnBehalfSelectorQuery).forEach(initOrderOnBehalfSelector);
};

const observer = new MutationObserver(() => {
    initCheckoutAddressSelectors();
    initOrderOnBehalfSelectors();
});

if (document.body) {
    observer.observe(document.body, {
        childList: true,
        subtree: true,
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        initCheckoutAddressSelectors();
        initOrderOnBehalfSelectors();
    });
} else {
    initCheckoutAddressSelectors();
    initOrderOnBehalfSelectors();
}
